import { useParams, Link } from 'react-router-dom';
import SEOMeta from '@/components/feature/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles, articleContents, featuredAnime, siteConfig } from '@/mocks/animeData';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '1', 10);

  const article = articles.find((a) => a.id === articleId) || articles[0];
  const content = articleContents[articleId] || articleContents[1];
  const relatedArticles = articles.filter((a) => a.id !== articleId).slice(0, 3);
  const relatedAnime = featuredAnime.slice(0, 3);

  if (!content) {
    return (
      <>
        <Navbar />
        <div className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-2xl text-secondary-500"></i>
            </div>
            <p className="text-foreground-600">文章未找到</p>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 mt-4 inline-block cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const title = article.title.split('——')[0];

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    dateModified: article.date,
    author: {
      '@type': 'Organization',
      name: siteConfig.name,
    },
    publisher: {
      '@type': 'Organization',
      name: siteConfig.name,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteConfig.url}/article/${articleId}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`${title}｜${siteConfig.name}`}
        description={article.excerpt}
        keywords={`成人动漫,${article.title},成人动漫推荐,${content.tags.join(',')}`}
        canonicalUrl={`${siteConfig.url}/article/${articleId}`}
        ogType="article"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <Navbar />
      <main className="pt-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="py-6">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: '文章推荐', path: '/#articles' },
                { name: title },
              ]}
            />
          </div>

          <article>
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="text-xs text-primary-500 bg-primary-50 px-3 py-1 rounded-full">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-400">{article.date}</span>
                <span className="text-xs text-foreground-400">{article.readTime}阅读</span>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-base text-foreground-600 leading-relaxed">{article.excerpt}</p>
            </div>

            <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="prose-custom">
              {content.sections.map((section, sIndex) => (
                <section key={sIndex} className="mb-8">
                  <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-4">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((paragraph, pIndex) => (
                    <p
                      key={pIndex}
                      className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4"
                    >
                      {paragraph}
                    </p>
                  ))}
                </section>
              ))}
            </div>

            <div className="flex items-center gap-2 flex-wrap mt-8 pt-6 border-t border-background-200/70">
              <span className="text-xs text-foreground-500">标签：</span>
              {content.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs bg-secondary-50 text-secondary-700 px-3 py-1 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </article>

          {(relatedArticles.length > 0 || relatedAnime.length > 0) && (
            <div className="mt-16 mb-14">
              <h3 className="font-heading text-xl text-foreground-950 mb-6">相关推荐</h3>

              {relatedArticles.length > 0 && (
                <div className="mb-10">
                  <h4 className="text-sm font-medium text-foreground-500 mb-4">相关文章</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {relatedArticles.map((ra) => (
                      <Link
                        key={ra.id}
                        to={`/article/${ra.id}`}
                        className="group bg-background-100 rounded-xl p-4 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                      >
                        <span className="text-xs text-primary-500 bg-primary-50 px-2 py-0.5 rounded-full mb-2 inline-block">
                          {ra.category}
                        </span>
                        <h5 className="font-heading text-sm text-foreground-900 mb-1 group-hover:text-primary-500 transition-colors line-clamp-2">
                          {ra.title}
                        </h5>
                        <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">{ra.excerpt}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {relatedAnime.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-foreground-500 mb-4">推荐动漫</h4>
                  <div className="grid grid-cols-3 gap-4">
                    {relatedAnime.map((anime) => (
                      <Link
                        key={anime.id}
                        to={`/article/${anime.id}`}
                        className="group bg-background-100 rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                      >
                        <div className="aspect-[3/4] overflow-hidden">
                          <img
                            src={anime.image}
                            alt={anime.title}
                            loading="lazy"
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-3">
                          <h5 className="font-heading text-sm text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
                            {anime.title}
                          </h5>
                          <span className="text-xs text-accent-500 font-bold">{anime.rating}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}